import { apiUrl } from '../config/api'

export enum FundSeriesTab {
  STRATEGY = 'strategy',
  PERFORMANCE = 'performance',
  QUOTE_VALUE = 'quote_value',
  PORTFOLIO = 'portfolio',
  CONTENT = 'content',
}

export interface ICollectorAccount {
  id: number
  bank_name: string
  cca: string
  cci: string
  id_spectrum_bank: string
  moneda: string
}

export interface IFundDraftData {
  id: number
  fund_id: number
  description: string | null
  has_scheduled_rescue: boolean
  allows_serie_selection: boolean
  card_url_web: string | null
  card_url_mobile: string | null
  header_url_web: string | null
  header_url_mobile: string | null
  vertical_ad_url: string | null
  horizontal_ad_url: string | null
  updated_at: string
}

export interface IFundDraft {
  fund_title: string
  has_scheduled_rescue: boolean
  draft: IFundDraftData
  collector_accounts: ICollectorAccount[]
}

export interface IFundSerie {
  serie_id: number
  serie_name: string
  serie_codigo: string
  fund_series_id: string
  status: string
  is_public: boolean
  published_at: string | null
}

export interface IStrategyTabItem {
  name: string
  color: string
  graphicValue: string
}

export interface IPortfolioTabItem {
  name: string
  color: string
  graphicValue: string
}

export interface IStrategyTab {
  data: IStrategyTabItem[]
  title: string
  visibility: boolean
  graphicTitle: string
}

export interface IPerformanceTab {
  visibility: boolean
  title?: string
}

export interface IPerformancePeriod {
  period: string
  value: string | number
}

export interface IPerformanceRecord {
  id: string
  date: string
  returns: IPerformancePeriod[]
  status: 'draft' | 'published'
  published_at: string | null
  created_at: string
  updated_at: string
}

export type IPerformanceSaveResponse = IPerformanceRecord[]

export interface IPortfolioTab {
  data: IPortfolioTabItem[]
  title: string
  visibility: boolean
}

export interface IYAxis {
  start?: number
  end?: number
  interval?: number
  strict?: boolean
}

export interface IQuoteValueGroupedItem {
  date: string
  quote_value: Record<string, number>
}

export interface IQuoteValuePaginatedResponse {
  data: IQuoteValueGroupedItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface IQuoteValueSerieItem {
  quote_date: string
  quote_value: number
}

export interface IQuoteValueSeriePaginatedResponse {
  data: IQuoteValueSerieItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface IQuoteValueTab {
  visibility?: boolean
  title?: string
  yAxis?: IYAxis
  evolution?: string[]
}

export interface IContentFeatureItem {
  label: string
  values: Record<string, string>
}

export interface IContentCommissionItem {
  name: string
  values: Record<string, string>
}

export interface ILegalSection {
  visibility?: boolean
  title?: string
  subtitle?: string
  description?: string
}

export interface IContentTab {
  channelAvailability?: Record<string, boolean>
  document?: string
  features?: {
    visibility?: boolean
    data?: IContentFeatureItem[]
  }
  commissions?: {
    visibility?: boolean
    title?: string
    data?: IContentCommissionItem[]
  }
  disclaimer?: {
    visibility?: boolean
    title?: string
    description?: string
  }
  legal?: ILegalSection
}

export interface IContentTabResponse {
  channelAvailability?: boolean
  document?: string
  features?: {
    visibility?: boolean
    data?: IContentFeatureItem[]
  }
  commissions?: {
    visibility?: boolean
    title?: string
    data?: IContentCommissionItem[]
  }
  disclaimer?: {
    visibility?: boolean
    title?: string
    description?: string
  }
  legal?: ILegalSection
}

export interface IFundSeriesResponse {
  series: IFundSerie[]
  tabs: {
    strategy: Record<string, IStrategyTab | null>
    performance: Record<string, IPerformanceTab | null>
    quote_value: Record<string, IQuoteValueTab | null>
    portfolio: Record<string, IPortfolioTab | null>
    content: Record<string, IContentTabResponse | null>
  }
}

export const FundDraftService = () => {
  const getDraft = (codFund: string): Promise<IFundDraft | null> =>
    apiUrl.get(`/fund/bo/${codFund}/draft`).then((res) => res.data)

  const uploadImage = (
    codFund: string,
    file: File,
    type: 'card' | 'header',
    platform: 'mobile' | 'web'
  ): Promise<{ url: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)
    formData.append('platform', platform)
    return apiUrl
      .post(`/fund/bo/${codFund}/draft/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data)
  }

  const uploadAd = (
    codFund: string,
    file: File,
    orientation: 'vertical' | 'horizontal'
  ): Promise<{ url: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('orientation', orientation)
    return apiUrl
      .post(`/fund/bo/${codFund}/draft/ad`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data)
  }

  const updateDraft = (
    codFund: string,
    payload: { description?: string; has_scheduled_rescue?: boolean }
  ): Promise<void> =>
    apiUrl.patch(`/fund/bo/${codFund}/draft`, payload).then((res) => res.data)

  const publishDraft = (codFund: string): Promise<{ success: boolean }> =>
    apiUrl.post(`/fund/bo/${codFund}/draft/publish`).then((res) => res.data)

  const getSeries = (codFund: string, publicOnly = false): Promise<IFundSeriesResponse> =>
    apiUrl
      .get(`/fund-series/${codFund}/series`, { params: { public_only: publicOnly } })
      .then((res) => res.data)

  const updateSeriesTab = (
    codFund: string,
    codSerie: string,
    tab: FundSeriesTab,
    payload: object
  ): Promise<void> =>
    apiUrl
      .put(`/fund-series/${codFund}/series/${codSerie}/tabs/${tab}`, payload)
      .then((res) => res.data)

  const publishSeriesTab = (
    codFund: string,
    codSerie: string,
    tab: FundSeriesTab
  ): Promise<void> =>
    apiUrl
      .post(`/fund-series/${codFund}/series/${codSerie}/tabs/${tab}/publish`)
      .then((res) => res.data)

  const getPerformanceRecords = (
    codFund: string,
    codSerie: string
  ): Promise<IPerformanceRecord[]> =>
    apiUrl
      .get(`/fund-series/${codFund}/series/${codSerie}/performance/all`)
      .then((res) => res.data)

  const savePerformanceRecord = (
    codFund: string,
    codSerie: string,
    payload: { items: { date: string; returns: IPerformancePeriod[] }[] }
  ): Promise<IPerformanceSaveResponse> =>
    apiUrl
      .put(`/fund-series/${codFund}/series/${codSerie}/performance`, payload)
      .then((res) => res.data)

  const publishPerformanceRecords = (
    codFund: string,
    codSerie: string
  ): Promise<IPerformanceSaveResponse> =>
    apiUrl
      .post(`/fund-series/${codFund}/series/${codSerie}/performance/publish`)
      .then((res) => res.data)

  const getQuoteValues = (
    codFund: string,
    params: { page?: number; limit?: number; filter?: string }
  ): Promise<IQuoteValuePaginatedResponse> =>
    apiUrl
      .get(`/fund-series/${codFund}/quote-values`, { params })
      .then((res) => res.data)

  const getQuoteValuesSerie = (
    codFund: string,
    codSerie: string,
    params: { page?: number; limit?: number; filter?: string; date?: string }
  ): Promise<IQuoteValueSeriePaginatedResponse> =>
    apiUrl
      .get(`/fund-series/${codFund}/series/${codSerie}/quote-values/paginated`, { params })
      .then((res) => res.data)

  const updateQuoteValueTab = (codFund: string, payload: IQuoteValueTab): Promise<void> =>
    apiUrl.put(`/fund-series/${codFund}/tabs/quote-value`, payload).then((res) => res.data)

  const publishQuoteValueTab = (codFund: string): Promise<void> =>
    apiUrl.post(`/fund-series/${codFund}/tabs/quote-value/publish`).then((res) => res.data)

  const updateContentTab = (codFund: string, payload: IContentTab): Promise<void> =>
    apiUrl.put(`/fund-series/${codFund}/tabs/content`, payload).then((res) => res.data)

  const publishContentTab = (codFund: string): Promise<void> =>
    apiUrl.post(`/fund-series/${codFund}/tabs/content/publish`).then((res) => res.data)

  const uploadDocumentDraft = (
    codFund: string,
    file: File,
    document_tag: string
  ): Promise<{ data: boolean }> => {
    const formData = new FormData()
    formData.append('file', file, file.name.replaceAll(' ', ''))
    formData.append('document_tag', document_tag)
    return apiUrl
      .post(`/fund/bo/${codFund}/document/draft`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data)
  }

  const publishDocumentDraft = (codFund: string): Promise<{ data: boolean }> =>
    apiUrl.post(`/fund/bo/${codFund}/document/draft/publish`).then((res) => res.data)

  const downloadDocumentDraft = (codFund: string, id: number): Promise<Blob> =>
    apiUrl
      .get(`/fund/bo/${codFund}/document/draft/${id}/download`, { responseType: 'blob' })
      .then((res) => res.data)

  const renameDocumentDraft = (
    codFund: string,
    items: { id: number; constancy_filename: string }[]
  ): Promise<void> =>
    apiUrl
      .patch(`/fund/bo/${codFund}/document/draft/filenames`, { items })
      .then((res) => res.data)

  const getDocumentDraft = (
    codFund: string
  ): Promise<
    {
      id: number
      document_tag: string
      constancy_filename: string | null
      constancy_url: string
      fund_id: string
      sign: boolean
      updated_at: string
    }[]
  > => apiUrl.get(`/fund/bo/${codFund}/document/draft`).then((res) => res.data)

  return {
    getDraft,
    updateDraft,
    uploadImage,
    uploadAd,
    publishDraft,
    getSeries,
    updateSeriesTab,
    publishSeriesTab,
    getPerformanceRecords,
    savePerformanceRecord,
    publishPerformanceRecords,
    getQuoteValues,
    getQuoteValuesSerie,
    updateQuoteValueTab,
    publishQuoteValueTab,
    updateContentTab,
    publishContentTab,
    uploadDocumentDraft,
    publishDocumentDraft,
    downloadDocumentDraft,
    renameDocumentDraft,
    getDocumentDraft,
  }
}
